import { useState, useEffect, useRef } from 'react';
import { apiFetch } from './config';

// The one tag input (Slice A, 2026-08-07).
//
// It exists because three surfaces had each hand-rolled this control and they
// had drifted — the same complaint lib/tags.ts makes about the write path, one
// layer up. This component owns SUGGESTION and SELECTION only; it never mints
// anything. Committing is the caller's job, and both callers now hand names to
// a server door that does find-or-create in one transaction:
//
//   - the capture modal passes `tags` to POST /api/read/:id/claims
//   - ObjectDetail posts to POST /api/objects/:id/tags
//
// That split is deliberate. A client that searches, then decides the tag is
// new, then creates it, races itself: two tabs typing "halverton" mint two tag
// objects, and the loser is invisible. The server can do it under one lock.
//
// Names are shown as typed and matched case-insensitively, mirroring
// canonicalTagName's rule server-side (lowercase, whitespace collapsed) — the
// display stays friendly, the stored form stays canonical.

export default function TagPicker({ tags, onChange, placeholder = 'add a tag', autoFocus = false }) {
  const [input, setInput] = useState('');
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Debounced because it fires per keystroke against a search endpoint; 180ms
  // is under the threshold where a dropdown feels laggy and well above the
  // rate that would put a request behind every letter.
  useEffect(() => {
    const q = input.trim();
    if (!q) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await apiFetch(`/api/objects?object_type=tag&q=${encodeURIComponent(q)}`);
        if (!res.ok) return;
        const rows = await res.json();
        if (cancelled) return;
        const lower = tags.map((t2) => t2.toLowerCase());
        setHits(rows.filter((r) => !lower.includes((r.content || '').toLowerCase())).slice(0, 6));
      } catch {
        /* suggestions are a convenience — a failed lookup must not block typing */
      }
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [input, tags]);

  const add = (name) => {
    const clean = String(name || '').trim();
    if (!clean) return;
    if (!tags.some((t) => t.toLowerCase() === clean.toLowerCase())) onChange([...tags, clean]);
    setInput('');
    setHits([]);
    setOpen(false);
  };

  const remove = (name) => onChange(tags.filter((t) => t !== name));

  return (
    <div className="wv-tagpicker" ref={boxRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={input}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => {
          setInput(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add(input);
          }
          // Backspace on an empty field removes the last chip — the convention
          // every chip input has, and its absence reads as a bug.
          if (e.key === 'Backspace' && !input && tags.length) remove(tags[tags.length - 1]);
          if (e.key === 'Escape') setOpen(false);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && (hits.length > 0 || input.trim()) && (
        <div className="wv-tagdrop">
          {hits.map((h) => (
            <div key={h.id} onMouseDown={(e) => { e.preventDefault(); add(h.content); }}>
              {h.content}
            </div>
          ))}
          {input.trim() && !hits.some((h) => (h.content || '').toLowerCase() === input.trim().toLowerCase()) && (
            <div
              className="wv-tagdrop-new"
              onMouseDown={(e) => { e.preventDefault(); add(input); }}
            >
              + new tag “{input.trim()}”
            </div>
          )}
        </div>
      )}
      {tags.length > 0 && (
        <div className="wv-tagchips">
          {tags.map((t) => (
            <span key={t} className="wv-tagchip">
              {t}
              <button type="button" onClick={() => remove(t)} aria-label={`Remove ${t}`}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
